import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'react-datepicker/dist/react-datepicker.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Close, WarningAmberOutlined } from '@mui/icons-material';
import { CloseButton, CreateChancel, MinLoader } from 'components';
import {
    FindError,
    FindLoad,
    FindSuccessItem,
    formatSeconds, Images,
    ToastSuccess,
} from 'utils';
import { httpRequestsOnErrorsActions, httpRequestsOnSuccessActions, sessionActions } from 'store';
import { CorrectionHistory } from './correctionHistory';
import { SegmentRow } from './editSessionTimelineSegmentRow';
import {
    eventsToSegments,
    hasRunningSegment,
    removePause,
    segmentsToEvents,
    TICK_INTERVAL_MS,
    totalActiveMs,
    totalPausedMs,
    tryAddPause,
    updateBoundary,
    validateSegments,
} from './editSessionTimeline.helpers';

const EDIT_ACTION = 'EDIT_SESSION_TIMELINE';

const VIEWS = { EDIT: 'edit', HISTORY: 'history' };

const DRAWER_TRANSITION = { type: 'spring', damping: 28, stiffness: 220 };

export const EditSessionTimeline = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const sessionById = useSelector((state) => state.session.sessionById);

    const [view, setView] = useState(VIEWS.EDIT);
    const [segments, setSegments] = useState([]);
    const [error, setError] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    const loading = FindLoad(EDIT_ACTION);
    const success = FindSuccessItem(EDIT_ACTION);
    const apiError = FindError(EDIT_ACTION);

    useEffect(() => {
        if (!open || !sessionById?.events) return;
        setSegments(eventsToSegments(sessionById.events));
        setError(null);
        setView(VIEWS.EDIT);
    }, [open, sessionById?.id]);

    useEffect(() => {
        if (!success) return;
        ToastSuccess('Session timeline updated');
        dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_ACTION));
        onClose();
    }, [success]);

    useEffect(() => () => dispatch(httpRequestsOnErrorsActions.removeError(EDIT_ACTION)), []);

    // Live ticker — only runs while a trailing running segment is present.
    useEffect(() => {
        if (!open || !hasRunningSegment(segments)) return;
        const id = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
        return () => clearInterval(id);
    }, [open, segments]);

    const totals = useMemo(() => {
        const active = Math.floor(totalActiveMs(segments, now) / 1000);
        const paused = Math.floor(totalPausedMs(segments, now) / 1000);
        return { active, paused, total: active + paused };
    }, [segments, now]);

    const handleUpdateBoundary = (boundaryIndex, date) => {
        setError(null);
        setSegments((prev) => updateBoundary(prev, boundaryIndex, date));
    };

    const handleAddPause = (idx) => {
        const updated = tryAddPause(segments, idx);
        if (!updated) {
            setError('Active period is too short to insert a pause');
            return;
        }
        setSegments(updated);
        setError(null);
    };

    const handleRemovePause = (idx) => {
        const updated = removePause(segments, idx);
        if (!updated) return;
        setSegments(updated);
        setError(null);
    };

    const handleOpenHistory = () => {
        dispatch(sessionActions.getSessionCorrections(sessionById?.id));
        setView(VIEWS.HISTORY);
    };

    const handleSubmit = () => {
        const validationErr = validateSegments(segments, sessionById?.status);
        if (validationErr) {
            setError(validationErr);
            return;
        }
        const events = segmentsToEvents(segments, sessionById?.status);
        dispatch(sessionActions.editSessionTimeline(sessionById?.id, { events }));
    };

    const errorText = error || apiError?.message || apiError?.error;
    const isLoading = !!loading?.length;

    return (
        <AnimatePresence>
            {open && (
                <div className="est-drawer-root">
                    <motion.div
                        className="est-drawer-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={isLoading ? undefined : onClose}
                    />
                    <motion.div
                        className="est-drawer"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={DRAWER_TRANSITION}
                    >
                        {view === VIEWS.HISTORY ? (
                            <CorrectionHistory onBack={() => setView(VIEWS.EDIT)} onClose={onClose} />
                        ) : (
                            <>
                                <div className="est-header">
                                    <div>
                                        <h3>Edit Session Timeline</h3>
                                        <p>Modify segments to correct the recorded time.</p>
                                    </div>
                                    <CloseButton handleCLic={onClose} />
                                </div>

                                <div className="est-summary">
                                    <div className="est-summary-stats">
                                        <span className="est-stat est-stat-active">
                                            <span className="est-dot est-dot-active" /> Active: {formatSeconds(totals.active)}
                                        </span>
                                        <span className="est-stat est-stat-paused">
                                            <span className="est-dot est-dot-paused" /> Paused: {formatSeconds(totals.paused)}
                                        </span>
                                    </div>
                                    <div className="est-summary-right">
                                        <span className="est-total">Total: {formatSeconds(totals.total)}</span>
                                        <button
                                            type="button"
                                            className="est-history-btn"
                                            title="Correction history"
                                            onClick={handleOpenHistory}
                                        >
                                            <img src={Images.historyTab} alt="history" />
                                        </button>
                                    </div>
                                </div>

                                <div className="est-body">
                                    <div className="est-segments-list">
                                        {segments.map((seg, idx) => (
                                            <SegmentRow
                                                key={seg.id}
                                                segments={segments}
                                                idx={idx}
                                                now={now}
                                                onUpdateBoundary={handleUpdateBoundary}
                                                onAddPause={handleAddPause}
                                                onRemovePause={handleRemovePause}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {errorText && (
                                    <div className="est-error">
                                        <WarningAmberOutlined fontSize="inherit" />
                                        <span>{errorText}</span>
                                    </div>
                                )}

                                <div className="est-footer">
                                    <CreateChancel
                                        loader={!!isLoading}
                                        create={'Save Changes'}
                                        chancel={'Cancel'}
                                        onCreate={handleSubmit}
                                        onClose={onClose}
                                        buttonWidth="255px"
                                    />
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
